import { Input } from '../../../../shared/components/FormControls';
import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import {
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckSquare2,
  Square,
  Search,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { getSystemById } from '../../data/ttrpgSystems';

/* Normalize a token's group name — empty string means ungrouped. */
const getTokenGroup = (token) => (token.group || '').trim();

function LoadTokensModal({
  visible,
  scenes,
  currentTtrpgSystem,
  onCancel,
  onImport,
  mode = 'light',
}) {
  const isDark = mode === 'dark';
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [expandedScenes, setExpandedScenes] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [isPhone, setIsPhone] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [importCount, setImportCount] = useState(1);

  useEffect(() => {
    const mediaQueryWidth = window.matchMedia('(max-width: 620px)');
    const handleMediaChange = () => setIsPhone(mediaQueryWidth.matches);
    handleMediaChange();
    mediaQueryWidth.addEventListener('change', handleMediaChange);
    return () =>
      mediaQueryWidth.removeEventListener('change', handleMediaChange);
  }, []);

  useEffect(() => {
    if (visible) {
      setSelectedKeys(new Set());
      setExpandedScenes(new Set((scenes || []).map((s) => s.id)));
      const allGroupKeys = new Set();
      for (const scene of scenes || []) {
        for (const token of scene.tokens || []) {
          const g = getTokenGroup(token);
          if (g) allGroupKeys.add(`${scene.id}:${g}`);
        }
      }
      setExpandedGroups(allGroupKeys);
      setSearchQuery('');
      setImportCount(1);
    }
  }, [visible, scenes]);

  const toggleToken = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleScene = (sceneId) => {
    setExpandedScenes((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
  };

  const toggleGroup = (sceneId, groupName) => {
    const key = `${sceneId}:${groupName}`;
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllInScene = (scene) => {
    const tokens = scene.tokens || [];
    if (!tokens.length) return;
    const keys = tokens.map((t) => `${scene.id}:${t.id}`);
    const allSelected = keys.every((k) => selectedKeys.has(k));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const toggleAllInGroup = (sceneId, groupTokensList) => {
    const keys = groupTokensList.map((t) => `${sceneId}:${t.id}`);
    const allSelected = keys.every((k) => selectedKeys.has(k));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const handleImport = () => {
    const tokensToImport = [];
    const count = Math.max(1, Math.round(Number(importCount) || 1));
    for (const scene of scenes || []) {
      for (const token of scene.tokens || []) {
        const key = `${scene.id}:${token.id}`;
        if (selectedKeys.has(key)) {
          for (let i = 0; i < count; i++) {
            tokensToImport.push({
              token,
              sourceTtrpgSystem: scene.ttrpgSystem || null,
            });
          }
        }
      }
    }
    onImport(tokensToImport);
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();

  /* Returns true if a token matches the search query (name or group). */
  const tokenMatchesSearch = (token) => {
    if (!normalizedSearch) return true;
    const name = (token.name || '').toLowerCase();
    const group = getTokenGroup(token).toLowerCase();
    return name.includes(normalizedSearch) || group.includes(normalizedSearch);
  };

  const isCrossSystem = (sceneTtrpgSystem) =>
    (sceneTtrpgSystem || null) !== (currentTtrpgSystem || null);

  /* Partition a token list into ungrouped tokens and named groups (insertion order). */
  const partitionByGroup = (tokens) => {
    const groupMap = new Map();
    const ungrouped = [];
    for (const token of tokens) {
      const g = getTokenGroup(token);
      if (g) {
        if (!groupMap.has(g)) groupMap.set(g, []);
        groupMap.get(g).push(token);
      } else {
        ungrouped.push(token);
      }
    }
    return { ungrouped, groupMap };
  };

  /* Reusable token row. */
  const renderTokenRow = (token, sceneId, crossSystem) => {
    const key = `${sceneId}:${token.id}`;
    const isChecked = selectedKeys.has(key);
    const showWarning = isChecked && crossSystem;
    return (
      <div key={token.id}>
        <div
          role='button'
          tabIndex={0}
          aria-label={`${isChecked ? 'Deselect' : 'Select'} token ${token.name || 'Unnamed Token'}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '5px 12px',
            cursor: 'pointer',
            background: isChecked
              ? isDark
                ? 'rgba(233,79,55,0.15)'
                : 'rgba(233,79,55,0.08)'
              : 'transparent',
          }}
          onClick={() => toggleToken(key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleToken(key);
            }
          }}
        >
          {isChecked ? (
            <CheckSquare2
              size={18}
              style={{ flex: '0 0 auto', color: '#e94f37' }}
            />
          ) : (
            <Square
              size={18}
              style={{
                flex: '0 0 auto',
                color: isDark ? 'rgba(246,247,235,0.4)' : '#aaa',
              }}
            />
          )}
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '14px',
            }}
          >
            {token.name || 'Unnamed Token'}
          </span>
        </div>
        {showWarning && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px',
              padding: '3px 12px 5px 38px',
              fontSize: '11px',
              color: '#e94f37',
              lineHeight: '1.4',
            }}
          >
            <AlertTriangle
              size={12}
              style={{ flex: '0 0 auto', marginTop: '1px' }}
            />
            <span>
              This token is from another TTRPG system. System-specific
              information will be lost.
            </span>
          </div>
        )}
      </div>
    );
  };

  const selectedCount = selectedKeys.size;
  const borderColor = isDark ? 'var(--onyx)' : '#e4E7C5';
  const bg = isDark ? 'var(--jet)' : '#f6f7eb';
  const textColor = isDark ? 'var(--ivory)' : 'var(--jet)';

  return (
    <Rodal
      animation='slideUp'
      visible={visible}
      onClose={onCancel}
      className={isDark ? 'df_rodal_dark' : ''}
      customStyles={{
        border: `2px solid ${borderColor}`,
        backgroundColor: bg,
        color: textColor,
        width: isPhone ? 'calc(100% - 34px)' : 520,
        height: isPhone ? 'calc(100% - 114px)' : 580,
        marginTop: isPhone ? '80px' : '',
      }}
    >
      <div
        className={`trialment_login_form ${isDark ? 'df_modal_form_dark' : ''}`}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div
          className='trialment_login_form_row'
          style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, textAlign: 'center' }}
        >
          Load Tokens
        </div>
        <div
          className='trialment_login_form_row'
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '10px',
            flexShrink: 0,
            width: '100%',
          }}
        >
          <Search size={16} style={{ flex: '0 0 auto', color: isDark ? 'rgba(246,247,235,0.5)' : '#aaa' }} />
          <Input
            type='text'
            placeholder='Search by name or group…'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1, width: '100%', minWidth: 0 }}
          />
        </div>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            paddingRight: isPhone ? '2px' : '0',
          }}
        >
          {!scenes || scenes.length === 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: isPhone ? 'calc(100% - 42px)' : '180px',
                fontSize: '14px',
                color: isDark ? 'rgba(246,247,235,0.6)' : '#777',
              }}
            >
              No other scenes available.
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                paddingBottom: '4px',
              }}
            >
              {scenes.map((scene) => {
                const allTokens = scene.tokens || [];
                /* When searching, only show tokens matching the query */
                const tokens = normalizedSearch
                  ? allTokens.filter(tokenMatchesSearch)
                  : allTokens;

                /* Hide scenes with no matching tokens when search is active */
                if (normalizedSearch && tokens.length === 0) return null;

                /* When searching, treat all scenes as expanded */
                const isExpanded = normalizedSearch
                  ? true
                  : expandedScenes.has(scene.id);
                const crossSystem = isCrossSystem(scene.ttrpgSystem);
                const sceneSystem = getSystemById(scene.ttrpgSystem);
                const sceneKeys = tokens.map((t) => `${scene.id}:${t.id}`);
                const selectedInScene = sceneKeys.filter((k) =>
                  selectedKeys.has(k),
                ).length;
                const allInSceneSelected =
                  tokens.length > 0 &&
                  sceneKeys.every((k) => selectedKeys.has(k));
                const { ungrouped, groupMap } = partitionByGroup(tokens);

                const sceneHeaderBg = allInSceneSelected
                  ? isDark
                    ? 'rgba(233,79,55,0.45)'
                    : 'rgba(233,79,55,0.18)'
                  : isDark
                    ? 'var(--onyx)'
                    : '#e8e9d8';

                return (
                  <div
                    key={scene.id}
                    style={{
                      border: `1px solid ${isDark ? '#3a3f42' : '#d5d7c0'}`,
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Scene header */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: isPhone ? 'wrap' : 'nowrap',
                        gap: '8px',
                        padding: '6px 8px',
                        background: sceneHeaderBg,
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'background 0.15s',
                      }}
                      role='button'
                      tabIndex={0}
                      onClick={() => toggleScene(scene.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleScene(scene.id);
                        }
                      }}
                    >
                      <span
                        style={{
                          flex: '0 0 auto',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {isExpanded ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </span>
                      <span
                        style={{
                          fontWeight: 700,
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {scene.name || 'Unnamed Scene'}
                      </span>
                      {sceneSystem && (
                        <span
                          style={{
                            fontSize: '17px',
                            padding: '1px 6px',
                            borderRadius: '10px',
                            background: '#e94f37',
                            color: '#fff',
                            fontWeight: 600,
                            flex: '0 0 auto',
                          }}
                        >
                          {sceneSystem.shortName}
                        </span>
                      )}
                      {tokens.length > 0 && (
                        <span
                          style={{
                            fontSize: '11px',
                            color: isDark ? 'rgba(246,247,235,0.6)' : '#666',
                            flex: '0 0 auto',
                            marginRight: '10px',
                          }}
                        >
                          {selectedInScene}/{tokens.length}
                        </span>
                      )}
                      {tokens.length > 0 && (
                        /* Stop propagation so clicking Select All doesn't collapse the scene */
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            flexShrink: 0,
                            marginLeft: isPhone ? 'auto' : 0,
                            width: isPhone ? '100%' : 'auto',
                            display: 'flex',
                            justifyContent: isPhone ? 'flex-end' : 'flex-start',
                          }}
                        >
                          <Button
                            label={
                              allInSceneSelected ? 'Deselect All' : 'Select All'
                            }
                            type={
                              allInSceneSelected ? 'cinnabar' : 'black-white'
                            }
                            onPress={() => toggleAllInScene(scene)}
                          />
                        </div>
                      )}
                    </div>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div style={{ paddingTop: '4px', paddingBottom: '4px' }}>
                        {tokens.length === 0 ? (
                          <div
                            style={{
                              padding: '6px 12px',
                              fontSize: '13px',
                              color: isDark ? 'rgba(246,247,235,0.5)' : '#888',
                            }}
                          >
                            No tokens in this scene.
                          </div>
                        ) : (
                          <>
                            {/* Named groups */}
                            {[...groupMap.entries()].map(
                              ([groupName, groupTokensList]) => {
                                const groupKey = `${scene.id}:${groupName}`;
                                /* When searching, treat all groups as expanded */
                                const isGroupExpanded = normalizedSearch
                                  ? true
                                  : expandedGroups.has(groupKey);
                                const groupKeys = groupTokensList.map(
                                  (t) => `${scene.id}:${t.id}`,
                                );
                                const allGroupSelected = groupKeys.every((k) =>
                                  selectedKeys.has(k),
                                );
                                const selectedInGroup = groupKeys.filter((k) =>
                                  selectedKeys.has(k),
                                ).length;

                                const groupHeaderBg = allGroupSelected
                                  ? isDark
                                    ? 'rgba(233,79,55,0.35)'
                                    : 'rgba(233,79,55,0.12)'
                                  : isDark
                                    ? 'rgba(255,255,255,0.04)'
                                    : 'rgba(0,0,0,0.04)';

                                return (
                                  <div
                                    key={groupName}
                                    style={{
                                      margin: '4px 8px',
                                      border: `1px solid ${isDark ? '#3a3f42' : '#d5d7c0'}`,
                                      borderRadius: '3px',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {/* Group header */}
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: isPhone ? 'wrap' : 'nowrap',
                                        gap: '6px',
                                        padding: '4px 8px',
                                        background: groupHeaderBg,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        transition: 'background 0.15s',
                                      }}
                                      role='button'
                                      tabIndex={0}
                                      onClick={() =>
                                        toggleGroup(scene.id, groupName)
                                      }
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === 'Enter' ||
                                          e.key === ' '
                                        ) {
                                          e.preventDefault();
                                          toggleGroup(scene.id, groupName);
                                        }
                                      }}
                                    >
                                      <span
                                        style={{
                                          flex: '0 0 auto',
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        {isGroupExpanded ? (
                                          <ChevronDown size={13} />
                                        ) : (
                                          <ChevronRight size={13} />
                                        )}
                                      </span>
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          fontSize: '17px',
                                          flex: 1,
                                          minWidth: 0,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {groupName}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: '11px',
                                          color: isDark
                                            ? 'rgba(246,247,235,0.6)'
                                            : '#666',
                                          flex: '0 0 auto',
                                          marginRight: '10px',
                                        }}
                                      >
                                        {selectedInGroup}/
                                        {groupTokensList.length}
                                      </span>
                                      <div
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          flexShrink: 0,
                                          marginLeft: isPhone ? 'auto' : 0,
                                          width: isPhone ? '100%' : 'auto',
                                          display: 'flex',
                                          justifyContent: isPhone
                                            ? 'flex-end'
                                            : 'flex-start',
                                        }}
                                      >
                                        <Button
                                          label={
                                            allGroupSelected
                                              ? 'Deselect All'
                                              : 'Select All'
                                          }
                                          type={
                                            allGroupSelected
                                              ? 'cinnabar'
                                              : 'black-white'
                                          }
                                          onPress={() =>
                                            toggleAllInGroup(
                                              scene.id,
                                              groupTokensList,
                                            )
                                          }
                                        />
                                      </div>
                                    </div>

                                    {/* Group token list */}
                                    {isGroupExpanded && (
                                      <div>
                                        {groupTokensList.map((token) =>
                                          renderTokenRow(
                                            token,
                                            scene.id,
                                            crossSystem,
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              },
                            )}

                            {/* Ungrouped tokens */}
                            {ungrouped.length > 0 && (
                              <div>
                                {ungrouped.map((token) =>
                                  renderTokenRow(token, scene.id, crossSystem),
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: isPhone ? 'wrap' : 'nowrap',
            gap: '10px',
            marginTop: '12px',
            marginBottom: '8px',
            flexShrink: 0,
          }}
        >
          <Button
            label='Cancel'
            type={isDark ? 'onyx' : 'black-white'}
            onPress={onCancel}
          />
          <Input
            type='number'
            min={1}
            step={1}
            value={importCount}
            onChange={(e) => {
              const val = Math.max(1, Math.round(Number(e.target.value) || 1));
              setImportCount(val);
            }}
            title='Number of times to import each selected token'
            style={{
              width: '46px',
              padding: '0.35rem 0.2rem',
              textAlign: 'center',
            }}
          />
          <Button
            label={selectedCount > 0 ? `Import ×${importCount} (${selectedCount})` : 'Import'}
            type='cinnabar'
            disabled={selectedCount === 0}
            onPress={handleImport}
          />
        </div>
      </div>
    </Rodal>
  );
}

export default LoadTokensModal;
